import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Divider, Flex, Row, Table, TableProps } from 'antd';

import DisabledElement from 'components/disabledElement/disabledElement';
import TableTotal from 'components/tableTotal/tableTotal';
import NoData from 'components/noData/noData';
import ProfitCapitalizationTransferComponent from './profitCapitalizationTransferComponent';

import { onExportProfitCapitalizationFractions, onGetProfitCapitalizationFractions } from '../services/profitCapitalization';
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';
import { downloadFile } from 'utils/downloadFile';

import { ProfitCapitalization, ProfitCapitalizationFraction } from '../models/profitCapitalization';

interface ProfitCapitalizationComponentProps {
	raiseCapital: ProfitCapitalization;
	onReload: () => void;
}

const ProfitCapitalizationComponent: FunctionComponent<ProfitCapitalizationComponentProps> = ({ raiseCapital, onReload }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [fractions, setFractions] = useState<ProfitCapitalizationFraction[]>([]);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<ProfitCapitalizationFraction>['columns'] = [
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},

		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <span style={{ textWrap: 'wrap' }}>{isRTL() ? name.ar || name.en : name.en || name.ar}</span>,
		},

		{
			title: t('COMMON.SHARES'),
			dataIndex: 'fractions_after_rounded_up',
			key: 'fractions_after_rounded_up',
			width: 150,
		},
		{
			title: t('ACTIONS.IBAN'),
			dataIndex: 'iban',
			key: 'iban',
			render: (iban) => (iban ? iban : t('COMMON.N_A')),
		},
	];

	useEffect(() => {
		if (raiseCapital && raiseCapital.fractions) {
			setFractions(raiseCapital.fractions.profit_capitalization_fractions || []);
			setTotalRecords(raiseCapital.fractions.pagination.total || 0);
		}
	}, [raiseCapital]);

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoading(true);

			const response = await onGetProfitCapitalizationFractions(raiseCapital.action.id, page);
			setFractions(response.profit_capitalization_fractions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const exportToExcelHandler = async () => {
		try {
			setIsExporting(true);

			const response = await onExportProfitCapitalizationFractions(raiseCapital.action.id);
			await downloadFile(
				response.excel_file_path,
				isRTL() ? 'قائمة_توزيع_رأس_المال_المدر_للأرباح.xlsx' : 'profit_capitalization_distribution_list.xlsx',
			);
			setIsExporting(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsExporting(false);
		}
	};

	return (
		<>
			<Row gutter={[16, 16]} className="mt-4">
				<Col span={24} md={12} lg={8}>
					<DisabledElement
						label={t('ACTIONS.CAPITAL_RAISE')}
						value={raiseCapital?.action.capital_raise_ratio}
						addonAfter={<span className="fw-semibold">%</span>}
					/>
				</Col>

				<Col span={24} md={12} lg={8}>
					<DisabledElement
						label={t('ACTIONS.TOTAL_NUMBER_OF_UNIT_ISSUED')}
						value={raiseCapital?.action.number_of_issued_units}
						addonAfter={<span>{t('COMMON.SHARES')}</span>}
					/>
				</Col>

				<Col span={24} md={12} lg={8}>
					<DisabledElement value={raiseCapital?.action.total_capital_raise} label={t('ACTIONS.TOTAL_CAPITAL_RAISE')} showPrice />
				</Col>

				<Col span={24} md={12} lg={8}>
					<DisabledElement label={t('ACTIONS.ELIGIBILITY_DATE')} value={dateFormat(raiseCapital.action.eligibility_date, true)} />
				</Col>

				<Col span={24} md={12} lg={8}>
					<DisabledElement label={t('ACTIONS.DISTRIBUTION_DATE')} value={dateFormat(raiseCapital.action.distribution_date, true)} />
				</Col>
			</Row>

			{raiseCapital?.fractions?.profit_capitalization_fractions && raiseCapital?.fractions?.profit_capitalization_fractions.length > 0 && (
				<>
					<Divider />

					<div>
						<h6 className="mb-0">{t('ACTIONS.TRANSACTION_FRACTIONS')}</h6>
						<p className="mb-0 text-light">{t('ACTIONS.TRANSACTION_FRACTIONS_DESC')}</p>
					</div>

					<Row gutter={[16, 16]} className="mt-4">
						<Col span={24} md={12} lg={6}>
							<DisabledElement
								label={t('ACTIONS.CAPITAL_RAISE')}
								value={raiseCapital?.action.capital_raise_ratio}
								addonAfter={<span className="fw-semibold">%</span>}
							/>
						</Col>

						<Col span={24} md={12} lg={6}>
							<DisabledElement
								label={t('ACTIONS.TOTAL_NUMBER_OF_UNIT_ISSUED')}
								value={raiseCapital?.action.number_of_issued_units}
								addonAfter={<span>{t('COMMON.SHARES')}</span>}
							/>
						</Col>

						<Col span={24} md={12} lg={6}>
							<DisabledElement
								label={t('ACTIONS.FRACTION_SHARES')}
								value={raiseCapital?.action.fraction_shares}
								addonAfter={<span>{t('COMMON.SHARES')}</span>}
							/>
						</Col>

						<Col span={24} md={12} lg={6}>
							<DisabledElement
								label={t('ACTIONS.OWNERS')}
								value={raiseCapital?.action.number_of_issued_units}
								addonAfter={<span>{t('COMMON.SHAREHOLDERS')}</span>}
							/>
						</Col>
					</Row>

					<Flex align="center" justify="space-between" className="mt-5 mb-3">
						<h6 className="mb-0 fw-medium">{t('ACTIONS.FRACTIONS_OWNERS')}</h6>

						<Button type="primary" size="middle" loading={isExporting} onClick={exportToExcelHandler}>
							{t('COMMON.EXPORT_TO_EXCEL')}
						</Button>
					</Flex>

					<Table
						columns={columns}
						dataSource={fractions}
						scroll={{ x: 600 }}
						rowKey="id"
						loading={isLoading}
						bordered
						pagination={{
							pageSize: 15,
							total: totalRecords,
							showSizeChanger: false,
							current: currentPage,
							onChange: fetchMoreData,
							showTotal: (total) => <TableTotal total={total} />,
						}}
						locale={{ emptyText: <NoData /> }}
						sticky
					/>

					<ProfitCapitalizationTransferComponent raiseCapital={raiseCapital} onReload={onReload} />
				</>
			)}
		</>
	);
};

export default ProfitCapitalizationComponent;
